import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, distinctUntilChanged, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ApiResponse } from 'src/app/model/apiResponse.model';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { AccountLinkData } from '../model/accountLinkData';
import { LoginResponse } from '../model/loginResponse.model';
import { PasswordResultModel } from '../model/PasswordResultModel.model';
import { PublicAccountSubmission } from '../model/publicAccountSubmission.model';
import { RecaptchaResponse } from '../model/recaptchaResponse.model';
import { StringKeyValue } from '../model/stringKeyValue.model';
import { UserModel } from '../model/userModel.model';
import { EncryptionService } from './encryption.service';
import { HttpErrorHandler } from './error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  headers: HttpHeaders;
  apiServer: string;
  newApiRoute: string;

  constructor(private http: HttpClient, private apiRef: ApiProviderService, private encryptionService: EncryptionService,
    private httpErrorHandler: HttpErrorHandler) {
    this.headers = new HttpHeaders();
    this.headers.append('Content-Type', 'application/json');
    this.headers.append('Accept', 'application/json');
    this.apiServer = 'api/PublicUser/';
    this.newApiRoute = 'api/Account/';
  }

  getAccountStatus(): Observable<boolean> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}AccountStatus`), { headers: this.headers }).pipe(map(json => {
      if (json) {
        return json.toString().toLowerCase() === 'true';
      } else {
        return false;
      }
    }));
  }

  getSecurityQuestions(userName: string): Observable<Array<StringKeyValue>> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}GetSecurityQuestions`, [{ Key: 'username', Value: userName }]),
      { headers: this.headers })
      .pipe(map((result) => result as Array<StringKeyValue>));

  }

  verifyCaptchaData(token: string): Observable<RecaptchaResponse> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}VerifyRecaptcha`, [{ Key: 'token', Value: token }]), 
    {headers: this.headers })
      .pipe(map(json => json as RecaptchaResponse));
  }

  validateUserAccount(userModel: UserModel): Observable<LoginResponse> {
    return this.http.post(this.apiRef.getApiUrl(`${this.apiServer}ValidateUser`), userModel)
      .pipe(map(json => json as LoginResponse));
  }

  validatePasswordRequisites(userModel: UserModel): Observable<PasswordResultModel> {
    const encryptSvc = new EncryptionService(this.apiRef);
    userModel.Pwd = encryptSvc.encryptAes(userModel.Pwd);

    return this.http.post<ApiResponse>(this.apiRef.getApiUrl(`${this.apiServer}VerifyPasswordReqs`),
      { headers: this.headers, body: userModel})
      .pipe(distinctUntilChanged())
      .pipe(map((res: ApiResponse) => {
        const result = res.result as PasswordResultModel;
        return result;
      }))
      .pipe(catchError(this.httpErrorHandler.handleError));
  }

  loginToACA(userModel: UserModel): Observable<LoginResponse> {
    return this.http.post(this.apiRef.getApiUrl(`${this.apiServer}SignIn`), userModel)
      .pipe(map(json => json as LoginResponse))
      .pipe(catchError(this.httpErrorHandler.handleError));
  }

  saveNewAccount(submission: PublicAccountSubmission): Observable<string> {
    // encrypt Password
    submission.Password = this.encryptionService.encryptAes(submission.Password);
    return this.http.post<any>(this.apiRef.getApiUrl(`${this.apiServer}SaveNewUserAccount`), { headers: this.headers, body: submission })
      .pipe(map(resp => resp.Uri.toString()))
  }

  linkNewAccount(submission: PublicAccountSubmission): Observable<ApiResponse> {
    const linkSubmission = {
      username: submission.Username,
      contactmodels: submission.Contacts,
      licenseLinks: submission.licenseLinks
    } as AccountLinkData;
    return this.http.post<ApiResponse>(this.apiRef.getApiUrl(`${this.newApiRoute}link`), linkSubmission, { headers: this.headers })
      .pipe(map(resp => {
        return resp;
      }))
      .pipe(catchError(this.httpErrorHandler.handleError));

  }
}
